import { Check, MessageSquarePlus } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    isAddToChatCapable,
    MAX_CHIPS_PER_CHAT,
    requestAddChipsToChat,
} from '@apps/web/features/admin_agent_dock/chip_add_source';
import {
    type ChipHandle,
    type ChipKind,
} from '@apps/web/features/admin_agent_dock/chip_registry';
import { readCurrentAdminAgentChatId } from '@apps/web/features/admin_agent_dock/use_active_tab_persistence';
import { toast } from '@apps/web/lib/app_notifications';
import { cn } from '@apps/web/lib/utils';

// Click-confirmed "+ add to chat" affordances (Linear's pattern) for
// hoverable entity rows and cards. Capability is registry-driven: a
// surface renders the affordance for a kind iff that chip kind is
// registered, so registering a new kind lights these up with no
// per-page plumbing. Feedback lives in the control itself for single
// adds (idle -> added / in chat) and in toasts for bulk adds — the
// dock stays silent.

type AddToChatState = 'idle' | 'pending' | 'added' | 'duplicate';

// How long the confirmation state ("Added" / "In chat") lingers
// before the control returns to idle.
const CONFIRMATION_RESET_MS = 1600;

function useConfirmationState(): {
    readonly state: AddToChatState;
    readonly setState: (state: AddToChatState) => void;
    readonly settle: (state: 'added' | 'duplicate') => void;
} {
    const [state, setState] = useState<AddToChatState>('idle');
    const resetTimerRef = useRef<number | null>(null);
    useEffect(
        () => () => {
            if (resetTimerRef.current !== null) {
                window.clearTimeout(resetTimerRef.current);
            }
        },
        [],
    );
    const settle = useCallback((settled: 'added' | 'duplicate') => {
        setState(settled);
        if (resetTimerRef.current !== null) {
            window.clearTimeout(resetTimerRef.current);
        }
        resetTimerRef.current = window.setTimeout(() => {
            setState('idle');
        }, CONFIRMATION_RESET_MS);
    }, []);
    return { state, setState, settle };
}

function chipLimitToastMessage(overflowed: number): string {
    return `Chat context holds at most ${MAX_CHIPS_PER_CHAT} chips — ${overflowed} not added`;
}

// Own an AbortController for the button's in-flight click and abort it
// on unmount. Row/card affordances live inside a route pane, so a
// navigation away tears the button down and the unmount cleanup runs —
// the queued handler is dropped from `chip_add_source`'s pending map
// before the dock can consume it into whatever chat is next active.
// Callers assign a fresh controller into the ref at click time and
// clear it when the click settles.
function usePendingAbortOnUnmount(): React.MutableRefObject<AbortController | null> {
    const abortRef = useRef<AbortController | null>(null);
    useEffect(() => {
        return () => {
            abortRef.current?.abort();
            abortRef.current = null;
        };
    }, []);
    return abortRef;
}

export function AddToChatButton({
    kind,
    entityId,
    resolveChip,
    showLabel = false,
    idleLabel,
    className,
}: {
    readonly kind: ChipKind;
    // Only used for stable test/automation handles; the chip itself
    // comes from `resolveChip` at click time.
    readonly entityId: string;
    readonly resolveChip: () => Promise<ChipHandle>;
    readonly showLabel?: boolean;
    // Overrides the idle "Add to chat" text — for surfaces that show
    // two affordances side by side (e.g. note + its source
    // conversation) and need to say which entity each one attaches.
    readonly idleLabel?: string;
    readonly className?: string;
}) {
    const { state, setState, settle } = useConfirmationState();
    const abortRef = usePendingAbortOnUnmount();
    const handleClick = useCallback(
        async (event: React.MouseEvent<HTMLButtonElement>) => {
            // Rows and cards are links (some draggable): the add must
            // neither navigate nor start a drag.
            event.preventDefault();
            event.stopPropagation();
            if (state === 'pending') {
                return;
            }
            setState('pending');
            // Abort any in-flight click that outlived the component or
            // the route (round-3 P2-1). The controller is cleared
            // when it settles or aborts, so the pending map in
            // `chip_add_source` never retains a stale handler that
            // could bind to whatever chat happens to be active later.
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            // Snapshot the intended chat id NOW, at click time. The
            // dock consumes this via `request.chatId` — a subsequent
            // tab switch between click and drain must not redirect
            // this write to a different chat (round-7 P1).
            const targetChatId = readCurrentAdminAgentChatId();
            try {
                const chip = await resolveChip();
                if (controller.signal.aborted) {
                    return;
                }
                const result = await requestAddChipsToChat([chip], {
                    signal: controller.signal,
                    chatId: targetChatId,
                });
                if (result.added > 0) {
                    settle('added');
                } else if (result.duplicate > 0) {
                    settle('duplicate');
                } else {
                    toast.warning(chipLimitToastMessage(result.overflowed), {
                        persistNotification: false,
                    });
                    setState('idle');
                }
            } catch {
                if (controller.signal.aborted) {
                    return;
                }
                toast.error('Could not add to agent chat', {
                    persistNotification: false,
                });
                setState('idle');
            } finally {
                if (abortRef.current === controller) {
                    abortRef.current = null;
                }
            }
        },
        [abortRef, resolveChip, setState, settle, state],
    );
    if (!isAddToChatCapable(kind)) {
        return null;
    }
    const label =
        state === 'added'
            ? 'Added'
            : state === 'duplicate'
              ? 'In chat'
              : (idleLabel ?? 'Add to chat');
    return (
        <button
            type="button"
            onClick={(event) => void handleClick(event)}
            onPointerDown={(event) => event.stopPropagation()}
            aria-label={
                state === 'duplicate'
                    ? 'Already in agent chat'
                    : (idleLabel ?? 'Add to agent chat')
            }
            title={
                state === 'duplicate'
                    ? 'Already in agent chat'
                    : (idleLabel ?? 'Add to agent chat')
            }
            data-testid="add-to-chat"
            data-chip-kind={kind}
            data-entity-id={entityId}
            data-add-state={state}
            className={cn(
                // The pseudo-element extends the 24px control to a
                // ~40px vertical hit area without visually growing it
                // (the horizontal inset stays 0 so it cannot overlap
                // the row's own link/checkbox targets).
                'relative inline-flex h-6 shrink-0 items-center gap-1 rounded-md border border-[#DBE3E3] bg-white px-1.5 text-[11px] leading-4 font-medium text-[#5B7B7B] shadow-xs transition-[opacity,background-color,color,border-color,scale] duration-150 after:absolute after:inset-x-0 after:-inset-y-2 hover:bg-[#F0F5F5] hover:text-[#002429] focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-[#5B7B7B] focus-visible:outline-none active:scale-[0.96]',
                state === 'added' &&
                    'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-700',
                state === 'duplicate' && 'text-[#829494]',
                className,
            )}
        >
            {/* Both icons stay mounted and cross-fade (opacity +
                scale + blur) so the added-confirmation feels like a
                state change, not a swap. */}
            <span className="relative size-3.5">
                <MessageSquarePlus
                    className={cn(
                        'absolute inset-0 size-3.5 transition-[opacity,scale,filter] duration-300 ease-[cubic-bezier(0.2,0,0,1)]',
                        state === 'added'
                            ? 'scale-25 opacity-0 blur-xs'
                            : 'scale-100 opacity-100 blur-none',
                    )}
                />
                <Check
                    className={cn(
                        'absolute inset-0 size-3.5 transition-[opacity,scale,filter] duration-300 ease-[cubic-bezier(0.2,0,0,1)]',
                        state === 'added'
                            ? 'scale-100 opacity-100 blur-none'
                            : 'scale-25 opacity-0 blur-xs',
                    )}
                />
            </span>
            {showLabel || state !== 'idle' ? (
                <span className="animate-in fade-in duration-150">{label}</span>
            ) : null}
        </button>
    );
}

export function AddSelectedToChatButton({
    selectedCount,
    resolveChips,
    onAdded,
    disabled = false,
}: {
    readonly selectedCount: number;
    // Resolves chips for (at most `MAX_CHIPS_PER_CHAT` of) the
    // current selection, in table order.
    readonly resolveChips: () => Promise<ReadonlyArray<ChipHandle>>;
    readonly onAdded?: () => void;
    readonly disabled?: boolean;
}) {
    const [isAdding, setIsAdding] = useState(false);
    const abortRef = usePendingAbortOnUnmount();
    const handleClick = useCallback(async () => {
        if (isAdding) {
            return;
        }
        setIsAdding(true);
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        // Snapshot the intended chat id NOW, at click time — same
        // rationale as single-add `AddToChatButton` (round-7 P1).
        const targetChatId = readCurrentAdminAgentChatId();
        try {
            const chips = await resolveChips();
            if (controller.signal.aborted) {
                return;
            }
            // Chips beyond the cap are counted into the overflow the
            // toast reports; the dock's atomic fit-or-reject may add
            // its own overflow on top.
            const unrequested = Math.max(0, selectedCount - chips.length);
            const result = await requestAddChipsToChat(chips, {
                signal: controller.signal,
                chatId: targetChatId,
            });
            const overflowed = result.overflowed + unrequested;
            if (result.added > 0) {
                const duplicateSuffix =
                    result.duplicate > 0
                        ? ` (${result.duplicate} already attached)`
                        : '';
                toast.success(
                    `Added ${result.added} to agent chat${duplicateSuffix}`,
                    { persistNotification: false },
                );
                onAdded?.();
            } else if (result.duplicate > 0 && overflowed === 0) {
                toast.info('Already in agent chat', {
                    persistNotification: false,
                });
            }
            if (overflowed > 0) {
                toast.warning(chipLimitToastMessage(overflowed), {
                    persistNotification: false,
                });
            }
        } catch {
            if (controller.signal.aborted) {
                return;
            }
            toast.error('Could not add to agent chat', {
                persistNotification: false,
            });
        } finally {
            if (abortRef.current === controller) {
                abortRef.current = null;
            }
            if (!controller.signal.aborted) {
                setIsAdding(false);
            }
        }
    }, [abortRef, isAdding, onAdded, resolveChips, selectedCount]);
    return (
        <Button
            variant="outline"
            size="sm"
            className="h-8"
            disabled={disabled || isAdding || selectedCount === 0}
            onClick={() => void handleClick()}
            data-testid="add-selected-to-chat"
        >
            <MessageSquarePlus className="size-3.5" />
            Add <span className="tabular-nums">{selectedCount}</span> to chat
        </Button>
    );
}
